<script setup lang="ts">
import { ref, watch, reactive } from 'vue'
import { Icon } from '@iconify/vue'
import ViewLayout from '@/views/layouts/ViewLayout.vue'
import RcCustomerSelect from '@/components/rc/common/RcCustomerSelect.vue'
import RcSiteSelect from '@/components/rc/common/RcSiteSelect.vue'
import type { ISiteIdentifier } from '@/components/rc/common/RcSiteSelect.vue'
import type { ICustomer } from '@/types/core/Customer'
import type { ICustomerSavingsFilters } from '@/types/cpm/filters'
import type { ICustomerSavingsRecord } from '@/types/cpm'
import { useOIDCStore } from '@/stores/oidc.store'
import CustomerSavingsTable from '@/components/cpm/CustomerSavingsTable.vue'
import CustomerSavingsDialog from '@/components/cpm/CustomerSavingsDialog.vue'

const { isAdminUser } = useOIDCStore()

const filters = reactive<ICustomerSavingsFilters>({
  site_id: undefined,
  year: undefined,
  page: 1,
  // A year holds at most 12 records (one per month), so a full year fits on one page.
  per_page: 12,
})

const selectedCustomer = ref<ICustomer | undefined>(undefined)
const selectedSite = ref<ISiteIdentifier | undefined>(undefined)

// Year filter options
// Savings are only recorded for past/current periods, so don't offer future years.
const currentYear = new Date().getFullYear()
const years = Array.from({ length: 5 }, (_, i) => currentYear - 4 + i)

const dialogVisible = ref(false)
const editingRecord = ref<ICustomerSavingsRecord | undefined>(undefined)

watch(selectedCustomer, () => {
  // Reset site selection when customer changes
  selectedSite.value = undefined
  filters.site_id = undefined
})

watch(selectedSite, (newSite) => {
  filters.site_id = newSite?.site_id
  filters.page = 1
})

// Reset to the first page whenever the year filter changes
watch(
  () => filters.year,
  () => {
    filters.page = 1
  }
)

const updateFilters = (newFilters: Partial<ICustomerSavingsFilters>) => {
  Object.assign(filters, newFilters)
}

const openAddDialog = () => {
  editingRecord.value = undefined
  dialogVisible.value = true
}

const openEditDialog = (record: ICustomerSavingsRecord) => {
  editingRecord.value = record
  dialogVisible.value = true
}
</script>

<template>
  <ViewLayout>
    <template #controls>
      <el-button v-if="isAdminUser" type="primary" @click="openAddDialog">Add Savings Record</el-button>
    </template>

    <div class="flex flex-col gap-6 flex-grow-1">
      <div class="flex gap-3 flex-wrap">
        <RcCustomerSelect v-model="selectedCustomer" class="w-[300px]" placeholder="Customer" />
        <RcSiteSelect
          v-model="selectedSite"
          :disabled="!selectedCustomer"
          class="w-[300px]"
          placeholder="Site"
          :customer-id="selectedCustomer?.id"
        />
      </div>

      <div v-if="!selectedSite" class="h-128 flex items-center justify-center gap-2 text-text-tertiary">
        <Icon icon="mdi:alert-circle-outline" height="20" />
        <span>Please select a customer and site to view savings records.</span>
      </div>

      <el-card v-else class="w-full flex flex-col flex-grow-1">
        <div class="flex justify-between items-center mb-4">
          <el-text type="info">MONTHLY CUSTOMER SAVINGS</el-text>
        </div>

        <div class="flex gap-3 mb-4">
          <el-select v-model="filters.year" placeholder="Year" class="w-[160px]" clearable>
            <el-option v-for="year in years" :key="year" :label="year.toString()" :value="year" />
          </el-select>
        </div>

        <CustomerSavingsTable
          :filters="filters"
          :is-admin="!!isAdminUser"
          @update-filters="updateFilters"
          @edit="openEditDialog"
        />
      </el-card>
    </div>

    <CustomerSavingsDialog
      v-model="dialogVisible"
      :record="editingRecord"
      :initial-customer="selectedCustomer"
      :initial-site="selectedSite"
    />
  </ViewLayout>
</template>

<style lang="scss" scoped>
:deep(.el-card__body) {
  display: flex;
  flex-direction: column;
  flex-grow: 1;
}
</style>
